/**
 * @license
 * Copyright (C) 2025 Guido Berhoerster <guido+voxit@berhoerster.name>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import "@voxit/voxit-web-components";

import voxitFrontendContextHelpStyleSheet from "../style/vrf-contexthelp.css" with { type: "css" };
import voxitFrontendResultsStyleSheet from "../style/vrf-results.css" with { type: "css" };
import voxitFrontendStackedLayerStyleSheet from "../style/vrf-stackedlayer.css" with { type: "css" };
import voxitFrontendStackedStyleSheet from "../style/vrf-stacked.css" with { type: "css" };
import voxitFrontendStatementCountStyleSheet from "../style/vrf-statementcount.css" with { type: "css" };
import voxitFrontendTabPanelStyleSheet from "../style/vrf-tabpanel.css" with { type: "css" };
import voxitFrontendTabsStyleSheet from "../style/vrf-tabs.css" with { type: "css" };
import voxitFrontendTabStyleSheet from "../style/vrf-tab.css" with { type: "css" };
import voxitFrontendTooltipStyleSheet from "../style/vrf-tooltip.css" with { type: "css" };
import voxitFrontendFlashStyleSheet from "../style/vrf-flash.css" with { type: "css" };

class VoxitFrontendChangeEvent extends Event {
    constructor(type, options = {}) {
        super(type, { bubbles: true });
    }
}

class VoxitFrontendStackedLayer extends HTMLElement {
    static observedAttributes = ["selected"];

    constructor() {
        super();

        const template = document.querySelector("#vrf-stackedlayer-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendStackedLayerStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
    }

    attributeChangedCallback(name, oldValue, newValue) {
        console.debug(
            `Attribute ${name} has changed from ${oldValue} to ${newValue}`,
        );
        switch (name) {
            case "selected":
                if (oldValue !== newValue && newValue !== null) {
                    this.dispatchEvent(
                        new VoxitFrontendChangeEvent(
                            "voxitfrontendchange"
                        )
                    );
                }
                break;
        }
    }

    get selected() {
        return this.hasAttribute("selected");
    }

    set selected(value) {
        if (value !== this.selected) {
            if (value) {
                this.setAttribute("selected", "");
            } else {
                this.removeAttribute("selected");
            }
        }
    }
}
window.customElements.define("vrf-stackedlayer", VoxitFrontendStackedLayer);

class VoxitFrontendStacked extends HTMLElement {
    constructor() {
        super();

        const template = document.querySelector("#vrf-stacked-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendStackedStyleSheet];
        shadow.addEventListener("slotchange", this);
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        this.shadowRoot.addEventListener("voxitfrontendchange", this);
    }

    disconnectedCallback() {
        this.shadowRoot.removeEventListener("voxitfrontendchange", this);
    }

    #updateLayers(selectedLayerElement = null) {
        selectedLayerElement ??= this.querySelector(":scope > vrf-stackedlayer[selected]");
        selectedLayerElement ??= this.querySelector(":scope > vrf-stackedlayer");
        for (const layerElement of this.querySelectorAll(":scope > vrf-stackedlayer")) {
            layerElement.selected = (layerElement === selectedLayerElement);
        }
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "slotchange":
                this.#updateLayers();
                break;
            case "voxitfrontendchange":
                if (ev.target.parentElement === this) {
                    this.#updateLayers(ev.target);
                }
                break;
        }
    }
}
window.customElements.define("vrf-stacked", VoxitFrontendStacked);

class VoxitFrontendTabPanel extends HTMLElement {
    #internals;

    constructor() {
        super();

        const template = document.querySelector("#vrf-tabpanel-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendTabPanelStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
        this.#internals = this.attachInternals();
    }

    connectedCallback() {
        this.#upgradeProperty("selected");
    }

    #upgradeProperty(property) {
        if (this.hasOwnProperty(property)) {
            let value = this[property];
            delete this[property];
            this[property] = value;
        }
    }

    get selected() {
        return this.#internals.states.has("selected");
    }

    set selected(value) {
        if (Boolean(value)) {
            this.#internals.states.add("selected");
        } else {
            this.#internals.states.delete("selected");
        }
    }
}
window.customElements.define("vrf-tabpanel", VoxitFrontendTabPanel);

class VoxitFrontendTab extends HTMLElement {
    static observedAttributes = ["selected"];

    constructor() {
        super();

        const template = document.querySelector("#vrf-tab-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendTabStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        for (const property of ["selected", "tabPanelTarget"]) {
            this.#upgradeProperty(property);
        }
        this.shadowRoot.addEventListener("click", this);
    }

    disconnectedCallback() {
        this.shadowRoot.removeEventListener("click", this);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        console.debug(
            `Attribute ${name} has changed from ${oldValue} to ${newValue}`,
        );
        switch (name) {
            case "selected":
                if (oldValue !== newValue && newValue !== null) {
                    this.dispatchEvent(
                        new VoxitFrontendChangeEvent(
                            "voxitfrontendchange"
                        )
                    );
                }
                break;
        }
    }

    #upgradeProperty(property) {
        if (this.hasOwnProperty(property)) {
            let value = this[property];
            delete this[property];
            this[property] = value;
        }
    }

    get selected() {
        return this.hasAttribute("selected");
    }

    set selected(value) {
        value = Boolean(value);
        if (value !== this.selected) {
            if (value) {
                this.setAttribute("selected", "");
                this.tabIndex = 0;
            } else {
                this.removeAttribute("selected");
                this.tabIndex = -1;
            }
        }
    }

    get tabPanelTarget() {
        return this.getAttribute("tabpaneltarget");
    }

    set tabPanelTarget(value) {
        this.setAttribute("tabpaneltarget", value);
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "click":
                this.selected = true;
                break;
        }
    }
}
window.customElements.define("vrf-tab", VoxitFrontendTab);

class VoxitFrontendTabs extends HTMLElement {
    constructor() {
        super();

        const template = document.querySelector("#vrf-tabs-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendTabsStyleSheet];
        shadow.addEventListener("slotchange", this);
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        this.addEventListener("keydown", this);
        this.shadowRoot.addEventListener("voxitfrontendchange", this);
    }

    disconnectedCallback() {
        this.shadowRoot.removeEventListener("voxitfrontendchange", this);
        this.removeEventListener("keydown", this);
    }

    #updateTabs(selectedTabElement = null) {
        selectedTabElement ??= this.querySelector(":scope > vrf-tab[selected]");
        selectedTabElement ??= this.querySelector(":scope > vrf-tab");
        for (const tabElement of this.querySelectorAll(":scope > vrf-tab")) {
            tabElement.selected = (tabElement === selectedTabElement);
        }
        const selectedTabPanelElement = this.getRootNode().getElementById(selectedTabElement?.tabPanelTarget);
        for (const tabPanelElement of this.querySelectorAll(":scope > vrf-tabpanel")) {
            tabPanelElement.selected = (tabPanelElement === selectedTabPanelElement);
        }
    }

    #selectNextTab() {
        const nextTabElement = this.querySelector(":scope > vrf-tab[selected] + vrf-tab:not([disabled], [inert], [hidden])");
        if (nextTabElement !== null) {
            nextTabElement.selected = true;
            nextTabElement.shadowRoot.querySelector("#tabbutton").focus();
        }
    }

    #selectPreviousTab() {
        const previousTabElement = this.querySelector(":scope > vrf-tab:not([disabled], [inert], [hidden]):has(+ vrf-tab[selected])");
        if (previousTabElement !== null) {
            previousTabElement.selected = true;
            previousTabElement.shadowRoot.querySelector("#tabbutton").focus();
        }
    }

    #selectFirstTab() {
        const firstTabElement = this.querySelector(":scope > vrf-tab:not([disabled], [inert], [hidden])");
        if (firstTabElement !== null) {
            firstTabElement.selected = true;
            firstTabElement.shadowRoot.querySelector("#tabbutton").focus();
        }
    }

    #selectLastTab() {
        const lastTabElement = this.querySelector(":scope > :nth-last-child(1 of vrf-tab:not([disabled], [inert], [hidden])");
        if (lastTabElement !== null) {
            lastTabElement.selected = true;
            lastTabElement.shadowRoot.querySelector("#tabbutton").focus();
        }
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "slotchange":
                this.#updateTabs();
                break;
            case "voxitfrontendchange":
                if (ev.target.parentElement === this) {
                    this.#updateTabs(ev.target);
                }
                break;
            case "keydown": {
                const writingMode = window.getComputedStyle(ev.target)["writing-mode"];
                switch (ev.key) {
                    case "ArrowLeft":
                        if (writingMode === "horizontal-tb") {
                            this.#selectPreviousTab();
                        }
                        break;
                    case "ArrowRight":
                        if (writingMode === "horizontal-tb") {
                            this.#selectNextTab();
                        }
                        break;
                    case "ArrowTop":
                        if (writingMode === "vertical-lr" ||
                                writingMode === "vertical-rl" ||
                                writingMode === "sideways-rl") {
                            this.#selectPreviousTab();
                        } else if (writingMode === "sideways-lr") {
                            this.#selectNextTab();
                        }
                        break;
                    case "ArrowDown":
                        if (writingMode === "vertical-lr" ||
                                writingMode === "vertical-rl" ||
                                writingMode === "sideways-rl") {
                            this.#selectNextTab();
                        } else if (writingMode === "sideways-lr") {
                            this.#selectPreviousTab();
                        }
                        break;
                    case "Home":
                        this.#selectFirstTab();
                        break;
                    case "End":
                        this.#selectLastTab();
                        break;
                }
                break;
            }
        }
    }
}
window.customElements.define("vrf-tabs", VoxitFrontendTabs);

class VoxitFrontendTooltip extends HTMLElement {
    static observedAttributes = ["value"];
    #tooltipElement;

    constructor() {
        super();

        const template = document.querySelector("#vrf-tooltip-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendTooltipStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
        this.#tooltipElement = shadow.querySelector("#tooltip");
    }

    connectedCallback() {
        const buttonElement = this.shadowRoot.querySelector("#anchor");
        buttonElement.addEventListener("mouseenter", this);
        buttonElement.addEventListener("mouseleave", this);
        if (!CSS.supports("position-anchor", "--anchor")) {
            window.addEventListener("resize", this);
            this.#tooltipElement.addEventListener("beforetoggle", this);
        }
    }

    disconnectedCallback() {
        const buttonElement = this.shadowRoot.querySelector("#anchor");
        buttonElement.removeEventListener("mouseleave", this);
        buttonElement.removeEventListener("mouseenter", this);
        if (!CSS.supports("position-anchor", "--anchor")) {
            window.removeEventListener("resize", this);
            this.#tooltipElement.removeEventListener("beforetoggle", this);
        }
    }

    #positionTooltipFallback() {
        const anchorElement = this.shadowRoot.querySelector("#anchor");
        const rect = anchorElement.getBoundingClientRect();
        const top = rect.top + rect.height + window.scrollY;
        const left = rect.left + window.scrollX;
        anchorElement.popoverTargetElement.style.setProperty("--anchored-inset", `${top}px auto auto ${left}px`);
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "beforetoggle": // FALLTHROUGH
            case "resize":
                this.#positionTooltipFallback()
                break;
            case "mouseenter":
                ev.target.popoverTargetElement.showPopover();
                break;
            case "mouseleave":
                ev.target.popoverTargetElement.hidePopover();
                break;
        }
    }
}
window.customElements.define("vrf-tooltip", VoxitFrontendTooltip);

class VoxitFrontendFlash extends HTMLElement {
    #messageElement;
    #flashTimer;

    constructor() {
        super();

        const template = document.querySelector("#vrf-flash-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendFlashStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
        this.#messageElement = shadow.querySelector("#message");
    }

    #onFlashTimer() {
        this.#messageElement.classList.remove("visible");
        this.#flashTimer = null;
    }

    show(duration = 2000) {
        this.#messageElement.classList.add("visible");
        clearTimeout(this.#flashTimer);
        this.#flashTimer = setTimeout(this.#onFlashTimer.bind(this), duration);
    }
}
window.customElements.define("vrf-flash", VoxitFrontendFlash);

class VoxitFrontendStatementCount extends HTMLElement {
    #conversationElement;

    constructor() {
        super();

        const template = document.querySelector("#vrf-statementcount-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendStatementCountStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        this.#conversationElement = this.closest("voxit-conversation");
        if (this.#conversationElement === null) {
            console.error("vrf-statementcount element must be a descendant of a voxit-conversation element");
            return;
        }
        this.#conversationElement.addEventListener("voxitstatechange", this);
        this.#conversationElement.addEventListener("voxitauthenticationsuccess", this);
        this.#conversationElement.addEventListener("voxitstatementschange", this);
        this.#conversationElement.addEventListener("voxitvotesubmitsuccess", this);
        this.#updateStatementCount();
        console.debug(`Associated with`, this.#conversationElement);
    }

    disconnectedCallback() {
        if (this.#conversationElement !== null) {
            this.#conversationElement.removeEventListener("voxitauthenticationsuccess", this);
            this.#conversationElement.removeEventListener("voxitstatechange", this);
            this.#conversationElement.removeEventListener("voxitstatementschange", this);
            this.#conversationElement.removeEventListener("voxitvotesubmitsuccess", this);
            this.#conversationElement = null;
        }
    }

    #updateStatementCount() {
        const client = this.#conversationElement.client;
        let number = 1;
        let total = client.conversation.seedStatements.size;
        if (client.statements.size > 0) {
            const otherStatementIDs = new Set(client.statements.keys());
            for (const id of client.participant.statements) {
                otherStatementIDs.delete(id);
            }
            total = otherStatementIDs.size;
            for (const id of client.participant.votes) {
                if (!client.participant.statements.has(id) && client.statements.has(id)) {
                    number++;
                }
            }
        }

        this.shadowRoot.querySelector("#statement-number").textContent = `${number}`;
        this.shadowRoot.querySelector("#statement-total").textContent = `${total}`;
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "voxitauthenticationsuccess": // FALLLTHROUGH
            case "voxitstatechange":           // FALLLTHROUGH
            case "voxitstatementschange":      // FALLLTHROUGH
            case "voxitvotesubmitsuccess":
                this.#updateStatementCount();
                break;
        }
    }
}
window.customElements.define("vrf-statementcount", VoxitFrontendStatementCount);

class VoxitFrontendResults extends HTMLElement {
    #conversationElement;

    constructor() {
        super();

        const template = document.querySelector("#vrf-results-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendResultsStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        this.#conversationElement = this.closest("voxit-conversation");
        if (this.#conversationElement === null) {
            console.error("vrf-results element must be a descendant of a voxit-conversation element");
            return;
        }
        this.#conversationElement.addEventListener("voxitstatechange", this);
        this.#conversationElement.addEventListener("voxitresultschange", this);
        console.debug(`Associated with`, this.#conversationElement);
    }

    disconnectedCallback() {
        if (this.#conversationElement !== null) {
            this.#conversationElement.removeEventListener("voxitresultschange", this);
            this.#conversationElement.removeEventListener("voxitstatechange", this);
            this.#conversationElement = null;
        }
    }

    #createGroupResultNodes(results) {
        const resultNodes = [];
        for (const type of ["agree", "disagree"]) {
            for (const result of results[type]) {
                const valuePercent = Math.round(result.value * 100);
                const resultItemTemplate = this.shadowRoot.querySelector(`#result-${type}-template`);
                const clone = resultItemTemplate.content.cloneNode(true);
                const statementText = result.statementText.length > 1000 ?
                        result.statementText.slice(0, 1000) + "\u2026" :
                        result.statementText;
                clone.querySelector(".result-statement").textContent = statementText;
                clone.querySelector(".result-value-percent").textContent = `${valuePercent}`;
                clone.querySelector(".result-bar").value = valuePercent;
                resultNodes.push(clone);
            }
        }
        resultNodes.sort((a, b) => {
            const aValue = a.querySelector(".result-bar").value;
            const bValue = b.querySelector(".result-bar").value;
            if (aValue > bValue) {
                return -1;
            } else if (aValue < bValue) {
                return 1;
            }
            return 0;
        });
        return resultNodes;
    }

    #updateResults() {
        const majorityListElement = this.shadowRoot.querySelector("#majority .results-list");
        majorityListElement.replaceChildren(...this.#createGroupResultNodes(this.#conversationElement.client.results.majority));

        for (const [i, tabElement] of this.shadowRoot.querySelectorAll("vrf-tab:not([tabpaneltarget='majority'])").entries()) {
            const resultGroup = this.#conversationElement.client.results.groups[i];
            const tabPanelElement = this.shadowRoot.getElementById(tabElement.tabPanelTarget);
            const resultsListElement = tabPanelElement.querySelector(".results-list");
            if (typeof resultGroup !== "undefined") {
                resultsListElement.replaceChildren(...this.#createGroupResultNodes(resultGroup));
            } else {
                // FIXME show placeholder
                resultsListElement.replaceChildren();
            }
        }
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "voxitstatechange":
                if (ev.state === "loaded") {
                    this.#updateResults();
                }
                break;
            case "voxitresultschange":
                this.#updateResults();
                break;
        }
    }
}
window.customElements.define("vrf-results", VoxitFrontendResults);

class VoxitFrontendContextHelp extends HTMLElement {
    #bubbleElement;

    constructor() {
        super();

        const template = document.querySelector("#vrf-contexthelp-template");
        const clone = template.content.cloneNode(true)
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendContextHelpStyleSheet];
        shadow.appendChild(clone);
        this.#bubbleElement = shadow.querySelector("#bubble");
    }

    connectedCallback() {
        if (!CSS.supports("position-anchor", "--anchor")) {
            window.addEventListener("resize", this);
            this.#bubbleElement.addEventListener("beforetoggle", this);
        }
    }

    disconnectedCallback() {
        if (!CSS.supports("position-anchor", "--anchor")) {
            window.removeEventListener("resize", this);
            this.#bubbleElement.removeEventListener("beforetoggle", this);
        }
    }

    #positionBubbleFallback() {
        const anchorElement = this.shadowRoot.querySelector("#help-button");
        const rect = anchorElement.getBoundingClientRect();
        const documentElement = document.documentElement;
        const right = documentElement.clientWidth - (rect.right + window.scrollX);
        const bottom = documentElement.clientHeight - (rect.top + window.scrollY);
        anchorElement.popoverTargetElement.style.setProperty("--anchored-inset", `auto ${right}px ${bottom}px auto`);
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "beforetoggle": // FALLTHROUGH
            case "resize":
                this.#positionBubbleFallback()
                break;
        }
    }
}
window.customElements.define("vrf-contexthelp", VoxitFrontendContextHelp);

class VoxitFrontend {
    conversationElement = null;
    stckedElement = null;
    errorDialog = null;
    loginDialog = null;
    states = Object.freeze({
        INITIAL: "initial",
        CONVERSATION_DIALOG: "conversation-form",
        LOADING: "loading",
        INTRO: "introduction",
        VOTING: "voting",
        STATEMENT_EDITOR: "statement-editing",
        INACTIVE: "inactive",
        ERROR: "error",
    });
    events = Object.freeze({
        OPEN_CONVERSATION_DIALOG: "open-conversation-form",
        LOAD: "load",
        LOADED: "loaded",
        ERROR: "error",
        SET_INACTIVE: "set-inactive",
        OPEN_STATEMENT_EDITOR: "open-statement-editing",
        CLOSE_STATEMENT_EDITOR: "close-statement-editing",
        START_VOTING: "start-voting",
    });
    transitions = {
        [this.states.INITIAL]: {
            [this.events.OPEN_CONVERSATION_DIALOG]: this.states.CONVERSATION_DIALOG,
            [this.events.LOAD]: this.states.LOADING,
        },
        [this.states.LOADING]: {
            [this.events.LOADED]: this.states.INTRO,
            [this.events.SET_INACTIVE]: this.states.INACTIVE,
            [this.events.ERROR]: this.states.ERROR,
        },
        [this.states.INTRO]: {
            [this.events.SET_INACTIVE]: this.states.INACTIVE,
            [this.events.START_VOTING]: this.states.VOTING,
        },
        [this.states.VOTING]: {
            [this.events.SET_INACTIVE]: this.states.INACTIVE,
            [this.events.OPEN_STATEMENT_EDITOR]: this.states.STATEMENT_EDITOR,
        },
        [this.states.STATEMENT_EDITOR]: {
            [this.events.SET_INACTIVE]: this.states.INACTIVE,
            [this.events.CLOSE_STATEMENT_EDITOR]: this.states.VOTING,
        },
        [this.states.INACTIVE]: {
        },
        [this.states.ERROR]: {
        },
    }
    state = this.states.INITIAL;
    voteCount = 0;

    constructor() {
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.get("conversation_id")) {
            this.state = this.transitionState(this.state, this.events.OPEN_CONVERSATION_DIALOG);
        } else {
            this.state = this.transitionState(this.state, this.events.LOAD);
        }

        this.interstitialVoteThresholds = INTERSTITIAL_VOTE_THRESHOLDS ?? [];

        this.renderState();
    }

    showConversationForm() {
        const urlParams = new URLSearchParams(window.location.search);
        document.forms.conversation_form.elements.conversation_id.value = urlParams.get("conversation_id") ?? "";
        document.querySelector("#conversation-dialog").showModal();
    }

    loadConversation() {
        const urlParams = new URLSearchParams(window.location.search);
        const conversationID = urlParams.get("conversation_id");

        const conversationClone = document.querySelector("#conversation-template").content.cloneNode(true);

        this.loginDialog = conversationClone.querySelector("#login-dialog");
        this.loginDialog.addEventListener("close", ev => {
            if (this.conversationElement.client.authenticationRequired && !this.conversationElement.client.authenticated) {
                ev.target.showModal();
            }
        })
        this.errorDialog = conversationClone.querySelector("#error-dialog");
        this.errorDialog.addEventListener("close", ev => {
            ev.target.showModal();
        })

        this.conversationElement = conversationClone.querySelector("voxit-conversation");
        this.conversationElement.addEventListener("voxitstatechange", this);
        this.conversationElement.addEventListener("voxitauthenticationchange", this);
        this.conversationElement.addEventListener("voxitauthenticationsuccess", this);
        this.conversationElement.addEventListener("voxitauthenticationerror", this);
        this.conversationElement.addEventListener("voxitvotesubmitsuccess", this);
        this.conversationElement.addEventListener("voxitvotesubmiterror", this);
        this.conversationElement.addEventListener("voxitstatementsubmitsuccess", this);
        this.conversationElement.addEventListener("voxitstatementsubmiterror", this);
        this.conversationElement.addEventListener("voxitconversationchange", this);
        this.conversationElement.addEventListener("voxitresultschange", this);
        this.conversationElement.addEventListener("click", this);
        this.conversationElement.setAttribute("conversation-id", conversationID);

        document.querySelector("main").appendChild(conversationClone);
    }

    updateResultsAvailable() {
        if (this.conversationElement.client.conversation.resultsAvailable) {
            this.conversationElement.classList.add("results-available");
        } else {
            this.conversationElement.classList.remove("results-available");
        }
    }

    updateStatementsAllowed() {
        if (this.conversationElement.client.conversation.statementsAllowed) {
            this.conversationElement.classList.add("statements-allowed");
        } else {
            this.conversationElement.classList.remove("statements-allowed");
        }
    }

    showStatementSubmittedFlash() {
        document.querySelector("#statement-submitted-flash").show();
    }

    showIntro() {
        document.querySelector("title").textContent = this.conversationElement.client.conversation.topic;
        document.querySelector("#intro").selected = true;
    }

    showVoting() {
        document.querySelector("title").textContent = this.conversationElement.client.conversation.topic;
        document.querySelector("#voting").selected = true;
        if (this.conversationElement.client.authenticationRequired && !this.conversationElement.client.authenticated) {
            this.loginDialog.showModal();
        }
    }

    showStatementEditing() {
        document.querySelector("#statement-editing").selected = true;
        if (this.conversationElement.client.authenticationRequired && !this.conversationElement.client.authenticated) {
            this.loginDialog.showModal();
        }
    }

    showInactive() {
        document.querySelector("title").textContent = this.conversationElement.client.conversation.topic;
        document.querySelector("#inactive").selected = true;
    }

    showError() {
        const selectedLayerElement = document.querySelector("#views > vrf-stackedlayer[selected]");
        if (selectedLayerElement !== null) {
            selectedLayerElement.selected = false;
        }
        this.errorDialog.querySelector("#error-details").textContent = this.conversationElement.error;
        this.errorDialog.showModal();
    }

    handleVote() {
        this.voteCount++;
        if (this.interstitialVoteThresholds.includes(this.voteCount)) {
            document.querySelector("#interstitial").showModal()
        }
    }

    renderState() {
        switch (this.state) {
            case this.states.CONVERSATION_DIALOG:
                this.showConversationForm();
                break;
            case this.states.LOADING:
                this.loadConversation();
                break;
            case this.states.INTRO:
                this.showIntro();
                break;
            case this.states.VOTING:
                this.showVoting();
                break;
            case this.states.STATEMENT_EDITOR:
                this.showStatementEditing();
                break;
            case this.states.INACTIVE:
                this.showInactive();
                break;
            case this.states.ERROR:
                this.showError();
                break;
        }
    }

    transitionState(state, ev) {
        if (typeof this.transitions[state][ev] === "undefined") {
            throw new Error(`Invalid state transition, state: ${this.state}, event: ${ev}`);
        }
        return this.transitions[state][ev];
    }

    handleEvent(ev) {
        console.debug("Event:", ev);
        switch (ev.type) {
            case "voxitstatechange":
                switch (ev.state) {
                    case "loaded":
                       if (this.conversationElement.client.conversation.isActive) {
                            this.state = this.transitionState(this.state, this.events.LOADED);
                            if (!document.querySelector("#intro")) {
                                this.state = this.transitionState(this.state, this.events.START_VOTING);
                            }
                            this.renderState();
                            this.updateStatementsAllowed();
                        } else {
                            this.state = this.transitionState(this.state, this.events.SET_INACTIVE);
                            this.renderState();
                        }
                        this.updateResultsAvailable();
                        break;
                    case "error":
                        this.state = this.transitionState(this.state, this.events.ERROR);
                        this.renderState();
                        break;
                }
                break;
            case "voxitauthenticationchange":
                if (ev.authenticationRequired && !ev.authenticated) {
                    this.loginDialog.showModal();
                }
                break;
            case "voxitauthenticationsuccess":
                this.loginDialog.close();
                break;
            case "voxitauthenticationerror":
                break;
            case "voxitvotesubmitsuccess":
                this.handleVote();
                break;
            case "voxitvotesubmiterror":
                break;
            case "voxitstatementsubmitsuccess":
                this.state = this.transitionState(this.state, this.events.CLOSE_STATEMENT_EDITOR);
                this.renderState();
                this.showStatementSubmittedFlash();
                break;
            case "voxitstatementsubmiterror":
                break;
            case "voxitconversationchange":
                if (!ev.conversation.isActive) {
                    this.state = this.transitionState(this.state, this.events.SET_INACTIVE);
                    this.renderState();
                }
                this.updateStatementsAllowed();
                break;
            case "voxitresultschange":
                this.updateResultsAvailable();
                break;
            case "click":
                switch (ev.target.closest("button")?.dataset.action) {
                    case "start-voting":
                        this.state = this.transitionState(this.state, this.events.START_VOTING);
                        this.renderState();
                        break;
                    case "add-statement":
                        this.state = this.transitionState(this.state, this.events.OPEN_STATEMENT_EDITOR);
                        this.renderState();
                        break;
                    case "close-statement":
                        this.state = this.transitionState(this.state, this.events.CLOSE_STATEMENT_EDITOR);
                        this.renderState();
                        break;
                    case "toggle-results":
                        break;
                }
                break;
        }
    }
}

new VoxitFrontend();
